import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getZoomDimensions,
  createCropSource,
  getWrapper,
  getScaledDimensions,
  loadImage
} from './imageHelper';

function useFetchImage(options) {
  const {
    src,
    srcLarge,
    wrapperHeight,
    wrapperWidth,
    attachmentHeight,
    attachmentWidth,
    disableZoom,
    expandToFill
  } = options;

  return useCallback(async () => {
    const wrapper = { width: wrapperWidth, height: wrapperHeight };
    const image = await loadImage(src);
    const largeImage = {
      width: attachmentWidth,
      height: attachmentHeight
    };

    if (expandToFill) {
      let croppedImage = createCropSource(src, wrapper, image);
      croppedImage = await loadImage(croppedImage.src);
      let croppedZoom = createCropSource(srcLarge, wrapper, largeImage, true);

      if (!disableZoom && croppedZoom) {
        croppedZoom = await loadImage(croppedZoom.src);
      }

      return { ...croppedImage, zoom: croppedZoom };
    }

    let zoom;
    if (!disableZoom) {
      zoom = {
        src: srcLarge,
        ...getZoomDimensions(image, largeImage, wrapper)
      };
    }
    return { zoom, ...image };
  }, [
    src,
    srcLarge,
    wrapperHeight,
    wrapperWidth,
    attachmentHeight,
    attachmentWidth,
    disableZoom,
    expandToFill
  ]);
}

export function useImage(options) {
  const {
    aspectRatio,
    containerId,
    disableZoom,
    expandToFill,
    zoomDimensions: [attachmentWidth = 0, attachmentHeight = 0],
    srcLarge,
    maxHeight,
    maxWidth,
    src
  } = options;

  const { width: wrapperWidth = 0, height: wrapperHeight = 0 } = getWrapper(
    containerId,
    maxWidth,
    maxHeight
  );
  const [loadedImage, setLoadedImage] = useState();
  const fetchImage = useFetchImage({
    src,
    srcLarge,
    wrapperHeight,
    wrapperWidth,
    attachmentHeight,
    attachmentWidth,
    disableZoom,
    expandToFill
  });
  const isNebula = useMemo(() => src && src.includes('isteam'), [src]);

  useEffect(() => {
    setLoadedImage(null);
  }, [src, aspectRatio, expandToFill]);

  useEffect(() => {
    if (isNebula) {
      let isActive = true;
      fetchImage().then(result => isActive && setLoadedImage(result));

      return () => {
        isActive = false;
      };
    }
  }, [fetchImage, isNebula, aspectRatio]);

  if (!isNebula) {
    return {
      width: wrapperWidth,
      height: wrapperHeight,
      source: src,
      zoom: null
    };
  }

  if (!loadedImage) {
    return { loading: true };
  }

  const dimensions = getScaledDimensions(
    { width: wrapperWidth, height: wrapperHeight },
    loadedImage
  );
  return {
    ...dimensions,
    source: loadedImage.src,
    zoom: loadedImage.zoom
  };
}

export function useResizeListener(renderCallback) {
  const [, setState] = useState(true);
  const forceUpdate = useCallback(() => setState(prev => !prev), []);

  useEffect(() => {
    window.addEventListener('resize', forceUpdate);
    if (renderCallback) {
      window.setTimeout(renderCallback, 0);
    }

    return () => window.removeEventListener('resize', forceUpdate);
  }, []);
}
